/**
 * Alpine.js component to control zoom functionality.
 * @returns {Object}
 */
export const zoomController = () => ({
	/**
	 * The body element to apply zoom styles to.
	 * @type {HTMLElement|null}
	 */
	body: null,

	/**
	 * The original zoom level.
	 * @type {number}
	 */
	originalZoom: 1,

	/**
	 * The current scale factor for zooming.
	 * @type {number}
	 */
	currentScaleFactor: 1,

	/**
	 * The maximum allowed scale factor.
	 * @type {number}
	 */
	maxScale: 2.5,

	/**
	 * The minimum allowed scale factor.
	 * @type {number}
	 */
	minScale: 0.8,

	/**
	 * The step size for each zoom adjustment.
	 * @type {number}
	 */
	step: 0.1,

	/**
	 * Increases the zoom level by 10%, up to the maximum scale.
	 *
	 * @returns {void}
	 */
	increaseZoom() {
		if (this.currentScaleFactor < this.maxScale) {
			this.currentScaleFactor += this.step;
			localStorage.setItem("zoomLevel", this.currentScaleFactor);
			this.setStyleToBody();
		}
	},

	/**
	 * Decreases the zoom level by 10%, down to the minimum scale.
	 * @returns {void}
	 */
	decreaseZoom() {
		if (this.currentScaleFactor > this.minScale) {
			this.currentScaleFactor -= this.step;
			localStorage.setItem("zoomLevel", this.currentScaleFactor);
			this.setStyleToBody();
		}
	},

	/**
	 * Resets the zoom level to the original value.
	 * @returns {void}
	 */
	resetZoom() {
		this.currentScaleFactor = this.originalZoom;
		localStorage.setItem("zoomLevel", this.currentScaleFactor);
		this.setStyleToBody();
	},

	/**
	 * Applies the current scale factor to the body element's zoom style.
	 * @returns {void}
	 */
	setStyleToBody() {
		this.body.style.zoom = this.currentScaleFactor;
	},

	/**
	 * Opens the zoom controls panel.
	 * @returns {void}
	 */
	open() {
		this.$store.zoom.isOpen = true;
	},

	/**
	 * Closes the zoom controls panel.
	 * @returns {void}
	 */
	close() {
		this.$store.zoom.isOpen = false;
	},

	/**
	 * Toggles the zoom controls panel visibility.
	 * @returns {void}
	 */
	toggle() {
		this.$store.zoom.isOpen = !this.$store.zoom.isOpen;
	},

	/**
	 * Initializes the component by setting up the body element and applying initial styles.
	 * @returns {void}
	 */
	init() {
		this.body = document.body;
		this.body.style.position = "relative";

		const storage = localStorage.getItem("zoomLevel");
		if (storage) {
			this.currentScaleFactor = parseFloat(storage);
		}
		this.setStyleToBody();
	},
});

/**
 * Alpine.js store to manage zoom panel visibility state.
 * @type {Object}
 */
export const zoom = {
	/**
	 * Indicates if the zoom controls panel is open.
	 * @type {boolean}
	 */
	isOpen: false,
};
